import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface Tournament {
    CLAVE: number;
    NOMBRE: string;
    FECHAINICIO?: string;
    TOTAL_INSCRITOS?: number;
}

interface Package {
    CLAVE: number;
    DESCRIPCION: string;
    COSTO: number;
}

interface PaymentRow {
    CLAVE: number;
    FECHA_PAGO: string;
    CANTIDAD: number;
    CVEFORMAPAGO: number; // 1=Cash, 2=Debit, 3=Credit, 4=Check, 5=Comp, 6=Zelle, 7=TAT Credit, 8=Refund, 9=In-Person
    FOURCC?: string | null;
    FICHANAL: number;
    NOMBRECOMPLETO: string;
    CVEJUGADOR: number;
}

interface PackageBowlerRow {
    CVEJUGADOR: number;
    FICHANAL: number;
    NOMBRECOMPLETO: string;
    CVEFORMAPAGO?: number | null;
    FOURCC?: string | null;
    FECHA_PAGO?: string | null;
}

const PAYMENT_LABELS: Record<number, string> = {
    1: 'Cash',
    2: 'Debit',
    3: 'Credit',
    4: 'Check',
    5: 'Comp',
    6: 'Zelle',
    7: 'TAT Credit',
    8: 'Refund',
    9: 'In-Person',
};

function paymentLabel(cve: number | null | undefined): string {
    if (!cve) return '—';
    return PAYMENT_LABELS[cve] ?? `#${cve}`;
}

function formatDate(raw: string | null | undefined): string {
    if (!raw) return '—';
    try {
        return new Date(raw).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
    } catch {
        return raw;
    }
}

const TournamentLists: React.FC = () => {
    const navigate = useNavigate();

    // Auth guard
    useEffect(() => {
        if (!localStorage.getItem('loggedInAdmin')) navigate('/admin');
    }, [navigate]);

    // ── Tournaments ────────────────────────────────────────────────────────────
    const [tournaments, setTournaments] = useState<Tournament[]>([]);
    const [tournamentsLoading, setTournamentsLoading] = useState(true);
    const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await fetch(`${BACKEND_URL}/api/admin/tournaments-with-counts`);
                const data = await res.json();
                setTournaments(data || []);
            } catch {
                setTournaments([]);
            } finally {
                setTournamentsLoading(false);
            }
        };
        load();
    }, []);

    // ── List mode tabs ─────────────────────────────────────────────────────────
    const [listMode, setListMode] = useState<'payments' | 'packages'>('payments');

    // ── Payment list state ─────────────────────────────────────────────────────
    const [payments, setPayments] = useState<PaymentRow[]>([]);
    const [paymentsLoading, setPaymentsLoading] = useState(false);
    const [paymentsError, setPaymentsError] = useState('');
    const [paymentFilter, setPaymentFilter] = useState<'all' | 'cash' | 'card' | 'check'>('all');
    const [paymentSearch, setPaymentSearch] = useState('');

    // ── Package list state ─────────────────────────────────────────────────────
    const [packages, setPackages] = useState<Package[]>([]);
    const [packagesLoading, setPackagesLoading] = useState(false);
    const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
    const [packageBowlers, setPackageBowlers] = useState<PackageBowlerRow[]>([]);
    const [packageBowlersLoading, setPackageBowlersLoading] = useState(false);
    const [packageSearch, setPackageSearch] = useState('');

    // ── Load data when tournament changes ──────────────────────────────────────
    useEffect(() => {
        if (!selectedTournament) {
            setPayments([]);
            setPackages([]);
            setSelectedPackage(null);
            setPackageBowlers([]);
            return;
        }

        // Load payments
        const loadPayments = async () => {
            setPaymentsLoading(true);
            setPaymentsError('');
            try {
                const res = await fetch(`${BACKEND_URL}/api/admin/tournament-payment-list/${selectedTournament.CLAVE}`);
                const data = await res.json();
                if (!res.ok) throw new Error(data.error || 'Failed to load payments');
                setPayments(Array.isArray(data) ? data : []);
            } catch (e: any) {
                setPaymentsError(e.message);
                setPayments([]);
            } finally {
                setPaymentsLoading(false);
            }
        };

        // Load packages for the dropdown
        const loadPackages = async () => {
            setPackagesLoading(true);
            try {
                const res = await fetch(`${BACKEND_URL}/api/admin/pos/available-packages/${selectedTournament.CLAVE}`);
                const data = await res.json();
                setPackages(data || []);
            } catch {
                setPackages([]);
            } finally {
                setPackagesLoading(false);
            }
        };

        setSelectedPackage(null);
        setPackageBowlers([]);
        loadPayments();
        loadPackages();
    }, [selectedTournament]);

    // ── Load bowlers when package changes ──────────────────────────────────────
    useEffect(() => {
        if (!selectedTournament || !selectedPackage) {
            setPackageBowlers([]);
            return;
        }
        const load = async () => {
            setPackageBowlersLoading(true);
            try {
                const res = await fetch(`${BACKEND_URL}/api/admin/tournament-package-list/${selectedTournament.CLAVE}/${selectedPackage.CLAVE}`);
                const data = await res.json();
                setPackageBowlers(Array.isArray(data) ? data : []);
            } catch {
                setPackageBowlers([]);
            } finally {
                setPackageBowlersLoading(false);
            }
        };
        load();
    }, [selectedTournament, selectedPackage]);

    // ── Filtered payment rows ──────────────────────────────────────────────────
    const filteredPayments = useMemo(() => {
        let rows = payments;
        if (paymentFilter === 'cash') rows = rows.filter(r => r.CVEFORMAPAGO === 1);
        else if (paymentFilter === 'card') rows = rows.filter(r => r.CVEFORMAPAGO === 2 || r.CVEFORMAPAGO === 3);
        else if (paymentFilter === 'check') rows = rows.filter(r => r.CVEFORMAPAGO === 4);
        if (paymentSearch.trim()) {
            const q = paymentSearch.trim().toLowerCase();
            rows = rows.filter(r =>
                r.NOMBRECOMPLETO?.toLowerCase().includes(q) ||
                String(r.FICHANAL) === q ||
                formatDate(r.FECHA_PAGO).toLowerCase().includes(q) ||
                (r.FOURCC && r.FOURCC.includes(q))
            );
        }
        return rows;
    }, [payments, paymentFilter, paymentSearch]);

    // ── Filtered package bowlers ───────────────────────────────────────────────
    const filteredPackageBowlers = useMemo(() => {
        if (!packageSearch.trim()) return packageBowlers;
        const q = packageSearch.trim().toLowerCase();
        return packageBowlers.filter(r =>
            r.NOMBRECOMPLETO?.toLowerCase().includes(q) ||
            String(r.FICHANAL) === q ||
            formatDate(r.FECHA_PAGO).toLowerCase().includes(q)
        );
    }, [packageBowlers, packageSearch]);

    // ── Payment totals ─────────────────────────────────────────────────────────
    const paymentTotals = useMemo(() => {
        const total = filteredPayments.reduce((s, r) => s + Number(r.CANTIDAD), 0);
        return { count: filteredPayments.length, total };
    }, [filteredPayments]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-100">
            {/* Header */}
            <div className="bg-white shadow-lg">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/admin/dashboard')}
                            className="text-gray-500 hover:text-gray-800 p-2 rounded-lg hover:bg-gray-100 transition"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <img src="/logo.avif" alt="TAT" className="h-10 w-auto" />
                        <div>
                            <h1 className="text-xl font-bold text-gray-900">Tournament Lists</h1>
                            {selectedTournament && (
                                <p className="text-sm text-gray-500 leading-tight">{selectedTournament.NOMBRE}</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">

                {/* Tournament picker */}
                <div className="bg-white rounded-2xl shadow-md p-5">
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                        Select Tournament
                    </label>
                    {tournamentsLoading ? (
                        <div className="flex items-center gap-2 text-gray-400 text-sm">
                            <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                            Loading…
                        </div>
                    ) : (
                        <select
                            value={selectedTournament?.CLAVE ?? ''}
                            onChange={e => {
                                const t = tournaments.find(t => t.CLAVE === Number(e.target.value));
                                setSelectedTournament(t || null);
                                setPaymentFilter('all');
                                setPaymentSearch('');
                                setPackageSearch('');
                            }}
                            className="w-full sm:w-auto min-w-64 border border-gray-300 rounded-xl px-3 py-2 text-sm focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition"
                        >
                            <option value="">— Choose a tournament —</option>
                            {tournaments.map(t => (
                                <option key={t.CLAVE} value={t.CLAVE}>
                                    {t.NOMBRE}{t.FECHAINICIO ? ` (${formatDate(t.FECHAINICIO)})` : ''}
                                </option>
                            ))}
                        </select>
                    )}
                </div>

                {selectedTournament && (
                    <>
                        {/* Mode tabs */}
                        <div className="bg-white rounded-2xl shadow-md">
                            <div className="flex border-b border-gray-100">
                                <button
                                    onClick={() => setListMode('payments')}
                                    className={`flex-1 sm:flex-none px-6 py-3.5 text-sm font-semibold rounded-tl-2xl transition ${
                                        listMode === 'payments'
                                            ? 'bg-purple-600 text-white'
                                            : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                                    }`}
                                >
                                    By Payment Type
                                </button>
                                <button
                                    onClick={() => setListMode('packages')}
                                    className={`flex-1 sm:flex-none px-6 py-3.5 text-sm font-semibold transition ${
                                        listMode === 'packages'
                                            ? 'bg-purple-600 text-white'
                                            : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                                    }`}
                                >
                                    By Package
                                </button>
                            </div>

                            {/* ── By Payment Type ───────────────────────────────── */}
                            {listMode === 'payments' && (
                                <div className="p-5">
                                    {/* Filters */}
                                    <div className="flex flex-col sm:flex-row gap-3 mb-4">
                                        <div className="flex gap-2 flex-wrap">
                                            {(['all', 'cash', 'card', 'check'] as const).map(f => (
                                                <button
                                                    key={f}
                                                    onClick={() => setPaymentFilter(f)}
                                                    className={`px-4 py-1.5 rounded-full text-xs font-semibold transition ${
                                                        paymentFilter === f
                                                            ? 'bg-purple-600 text-white shadow'
                                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                                                </button>
                                            ))}
                                        </div>
                                        <input
                                            type="text"
                                            value={paymentSearch}
                                            onChange={e => setPaymentSearch(e.target.value)}
                                            placeholder="Search name, TAT#, date, last4…"
                                            className="flex-1 border border-gray-200 rounded-xl px-3 py-1.5 text-sm focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition"
                                        />
                                    </div>

                                    {paymentsLoading ? (
                                        <div className="flex justify-center py-10">
                                            <div className="w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                                        </div>
                                    ) : paymentsError ? (
                                        <div className="text-red-600 text-sm bg-red-50 rounded-xl px-4 py-3">{paymentsError}</div>
                                    ) : filteredPayments.length === 0 ? (
                                        <div className="text-center py-10 text-gray-400">No payments found.</div>
                                    ) : (
                                        <>
                                            {/* Summary bar */}
                                            <div className="flex items-center justify-between mb-3 text-sm text-gray-500">
                                                <span>{paymentTotals.count} record{paymentTotals.count !== 1 ? 's' : ''}</span>
                                                <span className="font-semibold text-gray-700">
                                                    Total: ${paymentTotals.total.toFixed(2)}
                                                </span>
                                            </div>

                                            {/* Table */}
                                            <div className="overflow-x-auto rounded-xl border border-gray-100">
                                                <table className="w-full text-sm">
                                                    <thead>
                                                        <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                                            <th className="px-4 py-3">TAT #</th>
                                                            <th className="px-4 py-3">Name</th>
                                                            <th className="px-4 py-3">Date</th>
                                                            <th className="px-4 py-3 text-right">Amount</th>
                                                            <th className="px-4 py-3">Method</th>
                                                            <th className="px-4 py-3">Card (last 4)</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-50">
                                                        {filteredPayments.map(row => (
                                                            <tr key={row.CLAVE} className="hover:bg-purple-50/30 transition">
                                                                <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{row.FICHANAL}</td>
                                                                <td className="px-4 py-2.5 font-medium text-gray-900">{row.NOMBRECOMPLETO}</td>
                                                                <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{formatDate(row.FECHA_PAGO)}</td>
                                                                <td className="px-4 py-2.5 text-right font-semibold text-gray-900">${Number(row.CANTIDAD).toFixed(2)}</td>
                                                                <td className="px-4 py-2.5">
                                                                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                                                                        row.CVEFORMAPAGO === 1 ? 'bg-green-100 text-green-700' :
                                                                        row.CVEFORMAPAGO === 2 ? 'bg-blue-100 text-blue-700' :
                                                                        row.CVEFORMAPAGO === 3 ? 'bg-indigo-100 text-indigo-700' :
                                                                        row.CVEFORMAPAGO === 4 ? 'bg-yellow-100 text-yellow-700' :
                                                                        row.CVEFORMAPAGO === 5 ? 'bg-purple-100 text-purple-700' :
                                                                        row.CVEFORMAPAGO === 6 ? 'bg-teal-100 text-teal-700' :
                                                                        row.CVEFORMAPAGO === 7 ? 'bg-orange-100 text-orange-700' :
                                                                        row.CVEFORMAPAGO === 8 ? 'bg-red-100 text-red-700' :
                                                                        'bg-gray-100 text-gray-600'
                                                                    }`}>
                                                                        {paymentLabel(row.CVEFORMAPAGO)}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-2.5">
                                                                    {row.FOURCC ? (
                                                                        <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">•••• {row.FOURCC}</span>
                                                                    ) : (
                                                                        <span className="text-gray-300 text-xs">—</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* ── By Package ────────────────────────────────────── */}
                            {listMode === 'packages' && (
                                <div className="p-5">
                                    {/* Package selector */}
                                    <div className="flex flex-col sm:flex-row gap-3 mb-4">
                                        {packagesLoading ? (
                                            <div className="flex items-center gap-2 text-gray-400 text-sm">
                                                <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                                                Loading packages…
                                            </div>
                                        ) : (
                                            <select
                                                value={selectedPackage?.CLAVE ?? ''}
                                                onChange={e => {
                                                    const pkg = packages.find(p => p.CLAVE === Number(e.target.value));
                                                    setSelectedPackage(pkg || null);
                                                    setPackageSearch('');
                                                }}
                                                className="border border-gray-300 rounded-xl px-3 py-2 text-sm focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition min-w-64"
                                            >
                                                <option value="">— Select a package —</option>
                                                {packages.map(p => (
                                                    <option key={p.CLAVE} value={p.CLAVE}>
                                                        {p.DESCRIPCION} (${Number(p.COSTO).toFixed(2)})
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                        {selectedPackage && (
                                            <input
                                                type="text"
                                                value={packageSearch}
                                                onChange={e => setPackageSearch(e.target.value)}
                                                placeholder="Search name, TAT#, or date…"
                                                className="flex-1 border border-gray-200 rounded-xl px-3 py-1.5 text-sm focus:border-purple-400 focus:ring-2 focus:ring-purple-100 transition"
                                            />
                                        )}
                                    </div>

                                    {!selectedPackage ? (
                                        <div className="text-center py-10 text-gray-400">Select a package to see registered bowlers.</div>
                                    ) : packageBowlersLoading ? (
                                        <div className="flex justify-center py-10">
                                            <div className="w-6 h-6 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                                        </div>
                                    ) : filteredPackageBowlers.length === 0 ? (
                                        <div className="text-center py-10 text-gray-400">
                                            {packageSearch ? 'No matching bowlers.' : 'No bowlers registered for this package.'}
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex items-center justify-between mb-3 text-sm text-gray-500">
                                                <span>{filteredPackageBowlers.length} bowler{filteredPackageBowlers.length !== 1 ? 's' : ''}</span>
                                            </div>

                                            <div className="overflow-x-auto rounded-xl border border-gray-100">
                                                <table className="w-full text-sm">
                                                    <thead>
                                                        <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                                            <th className="px-4 py-3">#</th>
                                                            <th className="px-4 py-3">TAT #</th>
                                                            <th className="px-4 py-3">Name</th>
                                                            <th className="px-4 py-3">Date</th>
                                                            <th className="px-4 py-3">Payment</th>
                                                            <th className="px-4 py-3">Card (last 4)</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-50">
                                                        {filteredPackageBowlers.map((row, idx) => (
                                                            <tr key={row.CVEJUGADOR} className="hover:bg-purple-50/30 transition">
                                                                <td className="px-4 py-2.5 text-gray-400 text-xs">{idx + 1}</td>
                                                                <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{row.FICHANAL}</td>
                                                                <td className="px-4 py-2.5 font-medium text-gray-900">{row.NOMBRECOMPLETO}</td>
                                                                <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{formatDate(row.FECHA_PAGO)}</td>
                                                                <td className="px-4 py-2.5">
                                                                    {row.CVEFORMAPAGO ? (
                                                                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                                                                            row.CVEFORMAPAGO === 1 ? 'bg-green-100 text-green-700' :
                                                                            row.CVEFORMAPAGO === 2 ? 'bg-blue-100 text-blue-700' :
                                                                            row.CVEFORMAPAGO === 3 ? 'bg-indigo-100 text-indigo-700' :
                                                                            row.CVEFORMAPAGO === 4 ? 'bg-yellow-100 text-yellow-700' :
                                                                            row.CVEFORMAPAGO === 5 ? 'bg-purple-100 text-purple-700' :
                                                                            row.CVEFORMAPAGO === 6 ? 'bg-teal-100 text-teal-700' :
                                                                            row.CVEFORMAPAGO === 7 ? 'bg-orange-100 text-orange-700' :
                                                                            row.CVEFORMAPAGO === 8 ? 'bg-red-100 text-red-700' :
                                                                            'bg-gray-100 text-gray-600'
                                                                        }`}>
                                                                            {paymentLabel(row.CVEFORMAPAGO)}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-gray-300 text-xs">—</span>
                                                                    )}
                                                                </td>
                                                                <td className="px-4 py-2.5">
                                                                    {row.FOURCC ? (
                                                                        <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">•••• {row.FOURCC}</span>
                                                                    ) : (
                                                                        <span className="text-gray-300 text-xs">—</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}

                {!selectedTournament && !tournamentsLoading && (
                    <div className="bg-white rounded-2xl shadow-md p-12 text-center">
                        <svg className="w-12 h-12 text-purple-200 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                        <p className="text-gray-400 text-sm">Select a tournament above to view lists.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TournamentLists;
